import { useCallback, useRef, useState } from 'react'
import { useApi } from '../../../../hooks/useApi'
import { getStock, getPositions, getSkus } from '../Apis/api'
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE
} from '../configuration/stockMobileOptions'
import { ModalSelectionOption } from '../modalSelection'
import { Filter } from '../../../../types/filters'
import { Status } from '../../../../hooks/useFetchState'
import { ModalConfig, StockSearchContext, StockStep } from '../types/types'
import { ApiResponse } from '../../../../typings/ApiResponse'
import { Stock } from '../../../../interfaces'
import { eTypePosition } from '../../../../enums/eTypePosition'

const CLOSED: ModalConfig = {
  isOpen: false,
  title: '',
  description: '',
  highlightedValue: '',
  options: []
}

export const useStockSearch = () => {
  const { callApi: callApiWarehouses, state: stateWarehouses } = useApi()
  const { callApi: callApiBrands, state: stateBrands } = useApi()
  const { callApi: callApiStock, state: stateStock } =
    useApi<ApiResponse<Stock>>()

  const [activeStep, setActiveStep] = useState<StockStep>(StockStep.Search)
  const [modalConfig, setModalConfig] = useState<ModalConfig>(CLOSED)
  const [stockData, setStockData] = useState<ApiResponse<Stock> | null>(null)
  const [searchContext, setSearchContext] = useState<StockSearchContext | null>(
    null
  )
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: ''
  })

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }, [])

  const selectedOptionRef = useRef<ModalSelectionOption | null>(null)
  const pendingConfirmRef = useRef<
    ((opt: ModalSelectionOption) => void) | null
  >(null)

  const closeModal = useCallback(() => {
    setModalConfig(CLOSED)
    selectedOptionRef.current = null
    pendingConfirmRef.current = null
  }, [])

  const handleSelectionChange = useCallback((opt: ModalSelectionOption) => {
    selectedOptionRef.current = opt
  }, [])

  const handleConfirm = useCallback(() => {
    const opt = selectedOptionRef.current
    if (opt && pendingConfirmRef.current) {
      pendingConfirmRef.current(opt)
    }
  }, [])

  // ── Navegación stepper ────────────────────────────────────────────────────

  const goToSearch = useCallback(() => {
    setActiveStep(StockStep.Search)
    setStockData(null)
    setSearchContext(null)
  }, [])

  // ── 3. Stock (siempre el último llamado) ──────────────────────────────────

  const fetchStock = useCallback(
    (filters: Filter[], context: StockSearchContext) => {
      callApiStock(getStock(filters, DEFAULT_PAGE, DEFAULT_PAGE_SIZE))
        .then((res) => {
          setStockData(res ?? null)
          setSearchContext(context)
          setActiveStep(StockStep.Results)
        })
        .catch((err) =>
          setSnackbar({
            open: true,
            message: err
          })
        )
    },
    [callApiStock]
  )

  // ── Refetch stock por SKU al cambiar filtro de tipo de posición ───────────
  // Llamado desde StockBySkuResults cuando el usuario cambia el toggle
  const refetchStockBySku = useCallback(
    (context: StockSearchContext, typePositionId?: number) => {
      const filters: Filter[] = [
        {
          key: 'stockKeepingUnit',
          label: 'SKU',
          value: context.searchValue,
          displayValue: context.searchValue
        },
        {
          key: 'BrandId',
          label: 'Marca',
          value: context.selectedKey,
          displayValue: context.selectedLabel
        }
      ]
      if (typePositionId !== undefined) {
        filters.push({
          key: 'typePositionId',
          label: 'Tipo ubicación',
          value: typePositionId,
          displayValue: typePositionId.toString()
        })
      }
      callApiStock(getStock(filters, DEFAULT_PAGE, DEFAULT_PAGE_SIZE))
        .then((res) => setStockData(res ?? null))
        .catch((err) =>
          setSnackbar({
            open: true,
            message: err
          })
        )
    },
    [callApiStock]
  )
  // ── 1a. Búsqueda por Posición → 2. Bodegas → 3. Stock ─────────────────

  const searchByLocation = useCallback(
    (value: string) => {
      const locationFilter: Filter[] = [
        {
          key: 'positionName',
          label: 'Ubicación',
          value,
          displayValue: value
        }
      ]

      callApiWarehouses(
        getPositions(locationFilter, DEFAULT_PAGE, DEFAULT_PAGE_SIZE)
      )
        .then((res) => {
          const warehouses: ModalSelectionOption[] = (res?.items ?? []).map(
            (item: any) => ({
              key: item.warehouseId.toString(),
              label: item.warehouseName
            })
          )

          if (!warehouses.length) {
            setSnackbar({
              open: true,
              message:
                'No se encontraron coincidencias para la ubicación ingresada'
            })
            return
          }

          if (!warehouses.length) return

          if (warehouses.length === 1) {
            fetchStock(
              [
                {
                  key: 'positionCode',
                  label: 'Nombre Posición',
                  value: value,
                  displayValue: value
                },
                {
                  key: 'warehouseId',
                  label: 'Almacén',
                  value: warehouses[0].key,
                  displayValue: warehouses[0].label
                }
              ],
              {
                filterKey: 'location',
                searchValue: value,
                selectedLabel: warehouses[0].label,
                selectedKey: warehouses[0].key,
                locationActive: res?.items[0]?.active ?? true
              }
            )
            return
          }

          selectedOptionRef.current = warehouses[0]
          pendingConfirmRef.current = (selected) => {
            closeModal()
            fetchStock(
              [
                {
                  key: 'positionCode',
                  label: 'Nombre Posición',
                  value: value,
                  displayValue: value
                },
                {
                  key: 'warehouseId',
                  label: 'Almacén',
                  value: selected.key,
                  displayValue: selected.label
                }
              ],
              {
                filterKey: 'location',
                searchValue: value,
                selectedLabel: selected.label,
                selectedKey: selected.key,
                locationActive: res?.items[0]?.active ?? true
              }
            )
          }

          setModalConfig({
            isOpen: true,
            title: 'Confirma la bodega',
            description: 'existe en más de una bodega.',
            highlightedValue: value,
            options: warehouses
          })
        })
        .catch((err) =>
          setSnackbar({
            open: true,
            message: err
          })
        )
    },
    [callApiWarehouses, fetchStock, closeModal]
  )

  // ── 1b. Búsqueda por SKU → 2. Marcas → 3. Stock ──────────────────────────

  const searchBySku = useCallback(
    (value: string) => {
      const skuFilter: Filter[] = [
        {
          key: 'sku',
          label: 'SKU',
          value,
          displayValue: value
        }
      ]

      callApiBrands(getSkus(skuFilter, DEFAULT_PAGE, DEFAULT_PAGE_SIZE))
        .then((res) => {
          const items: any[] = res?.items ?? []
          const brands: ModalSelectionOption[] = (res?.items ?? []).map(
            (item: any) => ({
              key: item.productOriginId.toString(),
              label: item.brand
            })
          )
          if (!brands.length) {
            setSnackbar({
              open: true,
              message: 'No se encontraron coincidencias para el SKU ingresado'
            })
            return
          }

          // Tomamos fefo y vev del primer item (son propiedades del SKU, iguales en todos)
          const fefo: boolean = items[0]?.batch ?? false
          const vev: boolean = items[0]?.isGreenSale ?? false

          if (brands.length === 1) {
            fetchStock(
              [
                {
                  key: 'stockKeepingUnit',
                  label: 'SKU',
                  value: value,
                  displayValue: value
                },
                {
                  key: 'productOriginId',
                  label: 'Marca',
                  value: brands[0].key,
                  displayValue: brands[0].label
                },
                {
                  key: 'typePositionId',
                  label: 'Tipo ubicación',
                  value: eTypePosition.Venta,
                  displayValue: eTypePosition.Venta.toString()
                }
              ],
              {
                filterKey: 'sku',
                searchValue: value,
                selectedLabel: brands[0].label,
                selectedKey: brands[0].key,
                fefo,
                vev
              }
            )
            return
          }

          selectedOptionRef.current = brands[0]
          pendingConfirmRef.current = (selected) => {
            closeModal()
            fetchStock(
              [
                {
                  key: 'stockKeepingUnit',
                  label: 'SKU',
                  value: value,
                  displayValue: value
                },
                {
                  key: 'productOriginId',
                  label: 'Marca',
                  value: selected.key,
                  displayValue: selected.label
                },
                {
                  key: 'typePositionId',
                  label: 'Tipo ubicación',
                  value: eTypePosition.Venta,
                  displayValue: eTypePosition.Venta.toString()
                }
              ],
              {
                filterKey: 'sku',
                searchValue: value,
                selectedLabel: selected.label,
                selectedKey: selected.key,
                fefo,
                vev
              }
            )
          }

          setModalConfig({
            isOpen: true,
            title: 'Confirma la marca',
            description: 'está asociado a más de una marca.',
            highlightedValue: value,
            options: brands
          })
        })
        .catch((err) =>
          setSnackbar({
            open: true,
            message: err
          })
        )
    },
    [callApiBrands, fetchStock, closeModal]
  )

  // ── Dispatcher público ────────────────────────────────────────────────────

  const searchStock = useCallback(
    (value: string, filterKey: string) => {
      if (filterKey === 'positionName') searchByLocation(value)
      else if (filterKey === 'sku') searchBySku(value)
    },
    [searchByLocation, searchBySku]
  )

  return {
    activeStep,
    goToSearch,
    searchStock,
    refetchStockBySku,
    modalConfig,
    closeModal,
    handleSelectionChange,
    handleConfirm,
    stockData,
    searchContext,
    isLoading:
      stateWarehouses.status === Status.Loading ||
      stateBrands.status === Status.Loading ||
      stateStock.status === Status.Loading,
    stateStock,
    isRefetching: stateStock.status === Status.Loading,
    snackbar,
    closeSnackbar
  }
}
